import { z } from "zod";
import { branchPatternProblem, DEFAULT_BUG_BRANCH_PATTERN, DEFAULT_BUG_COLUMNS, isTimeZone, parseSheetUrl } from "./bugs.ts";

/** Request bodies for the orchestrator REST API, shared by the bot and dashboard. */

export const slugSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/, "lowercase letters, digits and dashes; must start with a letter or digit");

export const permissionModeSchema = z.enum(["default", "acceptEdits", "plan", "dontAsk", "auto", "bypassPermissions"]);

export const EFFORT_LEVELS = ["low", "medium", "high", "xhigh", "max"] as const;
export const effortSchema = z.enum(EFFORT_LEVELS);
export type EffortLevel = z.infer<typeof effortSchema>;

/** A path inside a folder: relative, forward slashes, no `..`, not inside `.git` or the `.wt` worktrees dir. */
export const relPathSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .transform((p) => p.replaceAll("\\", "/").replace(/\/+$/, ""))
  .refine((p) => {
    if (p.startsWith("/") || /^[a-zA-Z]:/.test(p)) return false;
    const parts = p.split("/");
    return parts.every((s) => s && s !== "." && s !== "..") && parts[0] !== ".git" && parts[0] !== ".wt";
  }, "a relative path inside the folder, without .. (and not .git or .wt)");

/** One repository in a multi-repo workspace: an existing sub-folder, or a clone into it. */
export const workspaceRepoSchema = z.object({
  path: relPathSchema,
  repoUrl: z.string().min(1).optional(),
});

/** An env file written into every new session's worktree; never committed. `repo: ""` is the project (or workspace) root. */
export const envFileSchema = z.object({
  repo: z.string().default(""),
  path: relPathSchema.default(".env"),
  content: z.string().max(100_000),
});

export type EnvFileInput = z.infer<typeof envFileSchema>;

/** Problems with a set of env files, given the repo folders they may target. */
export function envFileProblems(files: { repo: string; path: string }[], repos: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const f of files) {
    if (f.repo && !repos.includes(f.repo)) out.push(`env file ${f.path}: ${f.repo} is not one of the project's repositories`);
    const key = `${f.repo}/${f.path}`;
    if (seen.has(key)) out.push(`env file ${f.repo ? `${f.repo}/` : ""}${f.path} is listed twice`);
    seen.add(key);
  }
  return out;
}

export const createProjectSchema = z
  .object({
    name: z.string().min(1).max(100),
    slug: slugSchema,
    /**
     * `repo`: one git repository. `workspace`: a master folder holding several repositories
     * (e.g. frontend/ and backend/), each committed separately.
     */
    kind: z.enum(["repo", "workspace"]).default("repo"),
    /** Clone this repository into <projectsRoot>/<slug> (single-repo projects only). */
    repoUrl: z.string().min(1).optional(),
    /** Or use an existing folder under the projects root (for a workspace: the master folder, created if missing). */
    existingPath: z.string().min(1).optional(),
    /** Workspace only: the repositories inside the master folder. */
    repos: z.array(workspaceRepoSchema).max(20).default([]),
    /** Workspace only: CLAUDE.md for the master folder. Required unless the folder already has one. */
    claudeMd: z.string().max(100_000).optional(),
    setupCmd: z.string().optional(),
    defaultModel: z.string().optional(),
    defaultEffort: effortSchema.optional(),
    permissionMode: permissionModeSchema.default("acceptEdits"),
    allowedTools: z.array(z.string()).default([]),
    env: z.record(z.string(), z.string()).default({}),
    envFiles: z.array(envFileSchema).max(50).default([]),
    maxSessions: z.number().int().min(1).max(32).default(3),
    claudeMdTemplate: z.string().optional(),
  })
  .superRefine((p, ctx) => {
    const issue = (message: string) => ctx.addIssue({ code: "custom", message });
    if (p.repoUrl && p.existingPath) issue("give repoUrl or existingPath, not both");
    if (p.kind === "repo" && p.repos.length) issue("repos are only for workspace projects");
    if (p.kind === "workspace") {
      if (p.repoUrl) issue("a workspace takes a folder (existingPath), not a repoUrl; clone repositories into it through repos");
      if (!p.repos.length) issue("a workspace needs at least one repository");
      const paths = p.repos.map((r) => r.path);
      for (const a of paths) {
        if (paths.filter((b) => b === a).length > 1) issue(`repository folder ${a} is listed twice`);
        if (paths.some((b) => b !== a && b.startsWith(`${a}/`))) issue(`repository folders cannot be nested (${a})`);
      }
    }
    const repoPaths = p.kind === "workspace" ? p.repos.map((r) => r.path) : [];
    for (const m of new Set(envFileProblems(p.envFiles, repoPaths))) issue(m);
  });

export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  setupCmd: z.string().nullable().optional(),
  defaultModel: z.string().nullable().optional(),
  defaultEffort: effortSchema.nullable().optional(),
  permissionMode: permissionModeSchema.optional(),
  allowedTools: z.array(z.string()).optional(),
  env: z.record(z.string(), z.string()).optional(),
  envFiles: z.array(envFileSchema).max(50).optional(),
  maxSessions: z.number().int().min(1).max(32).optional(),
  claudeMdTemplate: z.string().nullable().optional(),
  channelId: z.string().nullable().optional(),
});

export const createSessionSchema = z.object({
  name: slugSchema,
  model: z.string().optional(),
  effort: effortSchema.optional(),
  permissionMode: permissionModeSchema.optional(),
  /** Branch every repository from this (in a workspace: each repo that has it). */
  baseBranch: z.string().optional(),
  threadId: z.string().optional(),
  /** Optional first prompt, sent as soon as the runner is up. */
  prompt: z.string().optional(),
  /** Run interactive Claude Code with Remote Control in tmux, so the session also shows in the Claude app. */
  remote: z.boolean().optional(),
  /**
   * Give the session its own branch (session/<name>) in a worktree. Off by default: the
   * session works in the project checkout on whatever branch it has. A baseBranch implies it.
   */
  newBranch: z.boolean().optional(),
  /**
   * Work on this exact branch in a worktree, after fetching origin: an existing branch (local
   * or origin's) is checked out and fast-forwarded, otherwise it is created from baseBranch.
   */
  branch: z.string().min(1).max(200).optional(),
});

export const sendMessageSchema = z.object({
  text: z.string().min(1),
  via: z.enum(["discord", "web", "system"]).default("web"),
  author: z.string().optional(),
});

export const answerPromptSchema = z.object({
  answer: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("question"), answers: z.record(z.string(), z.string()) }),
    z.object({
      kind: z.literal("permission"),
      decision: z.enum(["allow", "deny", "always"]),
      message: z.string().optional(),
    }),
  ]),
  via: z.enum(["discord", "web", "timeout", "system"]).default("web"),
  by: z.string().default("web"),
});

export const deleteSessionSchema = z.object({
  deleteBranch: z.boolean().default(false),
});

export const deleteProjectSchema = z.object({
  /** Also delete the project folder (the repositories, with any unpushed work) from the server. */
  deleteFiles: z.boolean().default(false),
});

/** A copy of a project's folder registered as a new project with the same settings. */
export const cloneProjectSchema = z.object({
  name: z.string().min(1).max(100),
  slug: slugSchema,
  /** Where the copy goes, relative to the projects root: a new folder, or an empty one. */
  path: relPathSchema,
});
export type CloneProjectInput = z.infer<typeof cloneProjectSchema>;

export const putSettingsSchema = z.record(z.string(), z.unknown());

// ── dashboard (Phase 3)

export const passwordSchema = z.object({ password: z.string().min(8, "at least 8 characters").max(200) });

export const changePasswordSchema = z.object({
  current: z.string(),
  next: z.string().min(8, "at least 8 characters").max(200),
});

/** `repo` picks one repository of a workspace session; empty or absent means the session root. */
const repoField = z.string().optional();

export const commitSchema = z.object({ message: z.string().min(1).max(5000), repo: repoField });

export const pushSchema = z.object({ repo: repoField });

export const pullRequestSchema = z.object({
  repo: repoField,
  title: z.string().min(1).max(250),
  body: z.string().max(60_000).optional(),
  base: z.string().optional(),
});

export const discordTestSchema = z.object({ channelId: z.string().optional() });

/** Folders under the projects root, for picking or creating a project folder from the dashboard. */
export const mkdirSchema = z.object({ path: relPathSchema });

// ── bug sheets

const sheetUrlSchema = z
  .string()
  .trim()
  .refine((u) => parseSheetUrl(u) !== null, "a Google Sheets link (https://docs.google.com/spreadsheets/d/…)");

const header = z.string().trim().min(1).max(200);

export const bugColumnsSchema = z.object({
  bug: header.default(DEFAULT_BUG_COLUMNS.bug),
  status: header.default(DEFAULT_BUG_COLUMNS.status),
  branch: header.default(DEFAULT_BUG_COLUMNS.branch),
  title: header.default(DEFAULT_BUG_COLUMNS.title),
  reportBug: header.default(DEFAULT_BUG_COLUMNS.reportBug),
  reportText: header.default(DEFAULT_BUG_COLUMNS.reportText),
  reportPr: header.default(DEFAULT_BUG_COLUMNS.reportPr),
});

const timeZoneSchema = z.string().trim().refine(isTimeZone, "an IANA time zone such as Asia/Kolkata");

export const createBugSheetSchema = z.object({
  name: z.string().trim().min(1).max(100),
  /** The bug sheet (a link to the right tab keeps its gid). */
  sheetUrl: sheetUrlSchema,
  /** Where each finished bug is written. */
  reportUrl: sheetUrlSchema,
  pollMinutes: z.number().int().min(1).max(1440).default(10),
  /** The daily branch's date is taken in this zone. */
  timezone: timeZoneSchema.default("UTC"),
  /** Bugs with this status (case-insensitive) are picked up. */
  openStatus: z.string().trim().min(1).max(100).default("Open"),
  columns: bugColumnsSchema.default(DEFAULT_BUG_COLUMNS),
  enabled: z.boolean().default(true),
});

export const updateBugSheetSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  sheetUrl: sheetUrlSchema.optional(),
  reportUrl: sheetUrlSchema.optional(),
  pollMinutes: z.number().int().min(1).max(1440).optional(),
  timezone: timeZoneSchema.optional(),
  openStatus: z.string().trim().min(1).max(100).optional(),
  columns: bugColumnsSchema.optional(),
  enabled: z.boolean().optional(),
});

const branchPatternSchema = z
  .string()
  .trim()
  .superRefine((p, ctx) => {
    const problem = branchPatternProblem(p);
    if (problem) ctx.addIssue({ code: "custom", message: problem });
  });

/** Bugs whose Branch column says `branch` are fixed in `project`, on a daily branch cut from `baseBranch`. */
export const createBugRouteSchema = z.object({
  branch: z.string().trim().min(1).max(200),
  /** Project ID or slug. */
  project: z.string().min(1),
  /** The git branch to start from and open the PR against; defaults to `branch`. */
  baseBranch: z.string().trim().max(200).optional(),
  branchPattern: branchPatternSchema.default(DEFAULT_BUG_BRANCH_PATTERN),
  /** For the day's session; absent means the project's. */
  permissionMode: permissionModeSchema.optional(),
});

export const updateBugRouteSchema = z.object({
  project: z.string().min(1).optional(),
  baseBranch: z.string().trim().max(200).nullable().optional(),
  branchPattern: branchPatternSchema.optional(),
  permissionMode: permissionModeSchema.nullable().optional(),
});

/** A Google service account key (the JSON file), or null to remove it. */
export const googleAccountSchema = z.object({ serviceAccount: z.string().max(20_000).nullable() });
